<template>
  <q-dialog v-model="isOpen" maximized transition-show="fade" transition-hide="fade" @hide="$emit('close')">
    <div class="lightbox-container">
      <!-- Header -->
      <div class="lightbox-header">
        <div class="left-header-section">
          <q-btn flat round dense icon="arrow_back" @click="$emit('close')" class="back-btn" />
          <div class="filename-container">
            <q-icon :name="getContentIcon(currentContentType)" size="20px" class="q-mr-sm" />
            <h4 class="filename">{{ currentImage.filename }}</h4>
          </div>
        </div>
        <div class="header-actions">
          <!-- AI Tools Dropdown for Images -->
          <q-btn v-if="currentContentType === 'image'" no-caps flat class="action-btn ai-tools-btn" color="grey-6">
            <q-icon name="o_auto_fix_high" class="q-mr-sm" color="grey-6" size="sm" />
            AI Tools
            <q-icon name="keyboard_arrow_down" class="q-ml-xs" color="grey-6" size="sm" />

            <q-menu anchor="bottom middle" self="top middle" class="gallery-menu" transition-show="jump-down" transition-hide="jump-up">
              <q-list style="width: auto" class="gallery-menu-list">
                <q-item clickable v-close-popup @click="$emit('edit', currentImage, true)" class="gallery-menu-item">
                  <q-item-section avatar>
                    <q-icon name="o_edit" color="black" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="gallery-menu-label custom-label">Edit with AI</q-item-label>
                  </q-item-section>
                </q-item>

                <q-item clickable v-close-popup @click="$emit('generateVideo', currentImage, true)" class="gallery-menu-item">
                  <q-item-section avatar>
                    <q-icon name="o_movie" color="black" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="gallery-menu-label custom-label">Generate Video</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>
          <q-btn v-if="currentContentType === 'image'" no-caps flat class="action-btn publish-btn" color="grey-6" @click="$emit('shareToMeta', currentImage)">
            <q-icon name="o_share" class="q-mr-sm" color="grey-6" size="sm" />
            Publish
          </q-btn>
          <q-btn no-caps flat class="primary-action-btn" @click="$emit('download', currentImage)" :loading="isDownloading">
            <template v-slot:loading>
              <q-spinner-dots color="secondary" size="24px" />
            </template>
            <q-icon name="o_download" class="q-mr-sm" color="secondary" size="sm" />
            Download
          </q-btn>
        </div>
      </div>

      <!-- Main content area -->
      <div class="lightbox-body">
        <!-- Sidebar panel with tools and content -->
        <div class="editor-layout">
          <!-- Content display area (images and videos) -->
          <div class="image-display-area">
            <!-- Video content -->
            <template v-if="currentContentType === 'video'">
              <!-- Show loading indicator when video is processing -->
              <div v-if="isVideoProcessing" class="video-processing-container">
                <div class="processing-indicator">
                  <q-circular-progress indeterminate size="60px" color="primary" track-color="grey-3" class="q-mb-md" />
                  <p class="processing-text">{{ processingMessage }}</p>
                  <p class="processing-subtext">This may take a few minutes... Please wait until the video process reach 100%.</p>
                </div>
              </div>

              <!-- Show video player when processing is complete or temp URL is available -->
              <VideoPlayer v-else :key="videoPlayerKey" :src="currentContentUrl" :options="videoPlayerOptions" />
            </template>

            <!-- Document content -->
            <template v-else-if="currentContentType === 'document'">
              <div class="document-display-area">
                <DocumentPreview
                  :key="`doc-${currentDocumentContent.id}-${currentDocumentContent.url}`"
                  :content="currentDocumentContent"
                  :documentProcessingStatuses="documentProcessingStatuses"
                  :isProcessing="isDocumentProcessing"
                  :processingMessage="documentProcessingMessage"
                  @viewer-loaded="onPdfViewerLoaded"
                />
              </div>
            </template>

            <!-- Image content -->
            <template v-else>
              <!-- Only show image after it's fully loaded (no waterfall effect) -->
              <div v-if="!imageError" class="image-container">
                <!-- Image is hidden until fully loaded -->
                <img v-if="imageLoaded" :src="currentImage.url" alt="Image preview" class="preview-image" />

                <!-- Loading state before image is ready -->
                <div v-if="!imageLoaded" class="image-loading-container">
                  <q-spinner color="primary" size="40px" />
                  <p class="q-mt-sm">Loading image...</p>
                </div>

                <!-- Hidden preload image to trigger events -->
                <img v-if="!imageLoaded" :src="currentImage.url" style="display: none" @load="handleImageLoaded" @error="handleImageError" />
              </div>

              <!-- Error state -->
              <div v-else class="image-error-container">
                <q-icon name="error_outline" size="48px" color="negative" />
                <p class="text-negative q-mt-md">Failed to load image</p>
                <q-btn color="primary" class="q-mt-md" label="Retry" @click="retryLoadImage" />
              </div>
            </template>
          </div>

          <!-- Content Sidebar (middle) -->
          <div class="sidebar-container">
            <div class="content-sidebar">
              <!-- Version Selector - appears at the top of every panel -->
              <VersionSelector
                v-if="currentImage && currentImage.id"
                :current-image="currentImage"
                :documentProcessingStatuses="documentProcessingStatuses"
                @version-added="$emit('version-added', $event)"
                @version-selected="handleVersionChange"
                @version-restored="handleVersionRestored"
                @version-changed="$emit('version-changed', $event)"
                @default-version-id="setDefaultVersionId"
              />
              <!-- Tool Content Container -->
              <div class="tool-content">
                <!-- Info panel -->
                <div v-show="activeTool === 'info'" class="info-panel">
                  <q-scroll-area class="scroll-area">
                    <!-- File details section - converted to dropdown -->
                    <q-expansion-item expand-separator icon="o_info" label="File Information" default-opened class="custom-expansion tool-title">
                      <div class="details-list">
                        <div class="detail-item">
                          <div class="detail-label">Type:</div>
                          <div class="detail-value">{{ currentContentType === 'video' ? 'Video' : getFileExtension(currentImage.filename) }}</div>
                        </div>
                        <div class="detail-item">
                          <div class="detail-label">Size:</div>
                          <div class="detail-value">{{ imageSizeMB }}</div>
                        </div>
                        <div class="detail-item">
                          <div class="detail-label">Resolution:</div>
                          <div class="detail-value">{{ currentImage.width }} x {{ currentImage.height }}</div>
                        </div>
                        <div v-if="currentContentType === 'video' && currentImage.duration" class="detail-item">
                          <div class="detail-label">Duration:</div>
                          <div class="detail-value">{{ formatDuration(currentImage.duration) }}</div>
                        </div>
                        <div class="detail-item">
                          <div class="detail-label">Uploader:</div>
                          <div class="detail-value">{{ currentImage.uploaded_by }}</div>
                        </div>
                        <div class="detail-item">
                          <div class="detail-label">Upload date:</div>
                          <div class="detail-value">{{ formatDate(currentImage.created_at) }}</div>
                        </div>
                      </div>
                    </q-expansion-item>

                    <!-- Tags section - converted to dropdown -->
                    <q-expansion-item expand-separator icon="o_local_offer" label="Tags" default-opened class="custom-expansion tool-title">
                      <div class="tags-container">
                        <div class="tags-list">
                          <div v-if="!currentImage.labels || currentImage.labels.length === 0" class="empty-state">
                            <p>No tags added yet</p>
                          </div>
                          <q-chip v-for="tag in currentImage.labels" :key="tag" removable :class="getTagChipClass(tag)" @remove="removeLabel(tag)">
                            <span class="tag-text" :title="tag">{{ formatTagText(tag) }}</span>
                          </q-chip>
                          <q-chip flat clickable class="add-tag-chip" @click="showAddLabelDialog = true">
                            <q-icon name="add" size="18px" class="q-mr-xs" />
                            Add tag
                          </q-chip>
                        </div>
                      </div>
                    </q-expansion-item>
                  </q-scroll-area>
                </div>

                <!-- Comments panel -->
                <div v-show="activeTool === 'comments'" class="comments-panel">
                  <!-- Comments expansion item with internal scroll -->
                  <q-expansion-item expand-separator icon="o_comment" label="Comments" default-opened class="custom-expansion tool-title comments-expansion">
                    <!-- Internal scroll area for comments content only -->
                    <q-scroll-area class="comments-scroll-area" ref="commentsScrollArea">
                      <!-- Loading State -->
                      <div v-if="commentsLoading" class="loading-spinner q-pa-md text-center">
                        <q-spinner-dots color="primary" size="2.5em" />
                        <p class="q-mt-sm text-grey-7">Loading comments...</p>
                      </div>
                      <!-- Error State -->
                      <div v-else-if="commentsError" class="error-message q-pa-md text-center">
                        <q-icon name="error_outline" color="negative" size="2em" />
                        <p class="q-mt-sm text-negative">Could not load comments. Please try again.</p>
                      </div>
                      <!-- No Comments State -->
                      <div v-else-if="!imageComments || imageComments.length === 0" class="empty-state">
                        <q-icon name="o_comment" size="48px" color="grey-4" />
                        <p class="text-subtitle1 q-mt-md">No comments yet</p>
                        <p class="text-caption text-grey-7">Comments will appear here when added</p>
                      </div>
                      <div v-else class="comments-list">
                        <div v-for="comment in imageComments" :key="comment.id" class="comment-item">
                          <div class="comment-layout">
                            <q-avatar size="36px" color="amber-3" text-color="grey-9" class="comment-avatar">
                              {{ comment.user_name ? comment.user_name.charAt(0).toUpperCase() : 'U' }}
                            </q-avatar>
                            <div class="comment-content-wrapper">
                              <div class="comment-header">
                                <div class="comment-author">{{ comment.user_name }}</div>
                                <div class="comment-time">
                                  {{ formatCommentDate(comment.created_at) }}
                                  <span v-if="isCommentEdited(comment)" class="comment-edited"> (edited {{ formatCommentDate(comment.updated_at) }}) </span>
                                </div>
                              </div>
                              <div class="comment-content">{{ comment.content }}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </q-scroll-area>
                  </q-expansion-item>

                  <!-- Comment input -->
                  <div class="comment-input-container">
                    <div class="comment-input-area">
                      <!-- Avatar - left aligned -->
                      <q-avatar size="40px" color="amber-3" text-color="grey-8" icon="o_person" class="comment-avatar" />

                      <!-- Comment input with button inside at bottom-right -->
                      <div class="comment-input-wrapper">
                        <!-- Text input -->
                        <q-input v-model="commentInput" placeholder="Add a comment..." type="textarea" rows="3" class="comment-input" bg-color="white" outlined @keyup.enter="handleAddComment">
                          <!-- Submit button placed inside the input at bottom-right -->
                          <template v-slot:after>
                            <q-btn
                              :color="commentInput.trim() ? 'video-chip-bg' : 'grey-4'"
                              :text-color="commentInput.trim() ? 'video-chip-text' : 'grey-7'"
                              class="submit-btn"
                              icon="send"
                              round
                              dense
                              :loading="addCommentMutation.isLoading.value"
                              :disable="!commentInput.trim()"
                              @click="handleAddComment"
                            />
                          </template>
                        </q-input>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tool Icons (right side) -->
            <div class="tools-sidebar">
              <div class="tool-icon" :class="{ active: activeTool === 'info' }" @click="toggleTool('info')">
                <q-icon name="o_info" size="24px" />
                <q-tooltip anchor="center left" self="center right" :offset="[10, 0]"> File Details </q-tooltip>
              </div>
              <div class="tool-icon" :class="{ active: activeTool === 'comments' }" @click="toggleTool('comments')">
                <q-icon name="o_comment" size="24px" />
                <q-tooltip anchor="center left" self="center right" :offset="[10, 0]"> Comments </q-tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog for adding tags - using BaseDialog -->
    <BaseDialog v-model="showAddLabelDialog" title="Add tag" confirm-label="Add" :loading="loadingLabel" :disabled="!newLabel.trim()" @confirm="handleAddLabel">
      <q-input v-model="newLabel" label="Tag name" autofocus outlined :error="!!labelError" :error-message="labelError" @keyup.enter="handleAddLabel" />
    </BaseDialog>
  </q-dialog>
</template>

<script>
import VersionSelector from './gallery/lightbox/version/VersionSelector.vue';
import { ref, computed, onMounted, onBeforeUnmount, watchEffect, watch } from '@vue/composition-api';
import helpers from '@/utils/globalMixins';
import BaseDialog from '@/components/base/BaseDialog.vue';
import VideoPlayer from './VideoPlayer.vue';
import DocumentPreview from './gallery/document/DocumentPreview.vue';
import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query';
import { ContentApi } from '@/api/modules/content';
import { getContentType } from '@/utils/fileUpload';

export default {
  name: 'GalleryLightbox',

  components: {
    BaseDialog,
    VersionSelector,
    VideoPlayer,
    DocumentPreview
  },

  mixins: [helpers],

  props: {
    content: {
      type: Array,
      required: true
    },
    startIndex: {
      type: Number,
      default: 0
    },
    documentProcessingStatuses: {
      type: Object,
      default: () => ({})
    }
  },

  setup(props, { emit, root }) {
    const isOpen = ref(true);
    const currentIndex = ref(props.startIndex);
    const showAddLabelDialog = ref(false);
    const newLabel = ref('');
    const loadingLabel = ref(false);
    const labelError = ref('');
    const imageSizeMB = ref('Calculating...');
    const activeTool = ref('info');
    const commentInput = ref('');
    const isDownloading = ref(false);
    const isImageLoading = ref(true);
    const imageLoaded = ref(false);
    const imageError = ref(false);
    const videoPlayerKey = ref(0);
    const commentsScrollArea = ref(null);
    // Store local modifications to avoid prop mutations
    const localImageModifications = ref({});

    // New state variables for coordinated version switching
    const versionChangeInProgress = ref(false);
    const versionChangeMutex = ref(false);

    // Local copy of the content prop to avoid direct prop mutation
    const internalContent = ref([...props.content]);

    // Watch for changes in props.content and update internal content
    watch(
      () => props.content,
      newContent => {
        internalContent.value = [...newContent];
      },
      { deep: true }
    );

    // Create computed property for current content (image or video) that respects one-way data flow
    const currentImage = computed(() => {
      const baseContent = internalContent.value[currentIndex.value] || {
        url: '/placeholder.svg?height=800&width=1200',
        title: 'Forest Gump',
        aspect_ratio: 1.5,
        labels: [],
        comments: [],
        type: 'image'
      };

      // Get the current content ID
      const contentId = baseContent.id;

      // If we have local modifications for this content, apply them
      const modifications = contentId ? localImageModifications.value[contentId] : null;

      if (modifications) {
        // Create a deep merged copy to avoid prop mutations
        return { ...baseContent, ...modifications };
      }

      // Return clean copy of the original to avoid prop mutations
      return { ...baseContent };
    });

    // Computed properties for content type detection
    const currentContentType = computed(() => {
      // Always use the utility function for proper content type detection
      // Don't trust the 'type' field alone since documents might be stored as type='image' in backend
      // Use selectedVersionContent to ensure content type detection works for selected versions
      const contentToCheck = selectedVersionContent.value || currentImage.value;
      if (contentToCheck) {
        return getContentType(contentToCheck);
      }
      return 'image';
    });

    const isCurrentContentVideo = computed(() => {
      return currentContentType.value === 'video';
    });

    // Track the currently selected version ID for comments
    const selectedVersionId = ref(null);

    // Store the actual selected version data
    const selectedVersionData = ref(null);

    // Function to set the default version ID when received from VersionSelector
    function setDefaultVersionId(id) {
      defaultVersionId.value = id;

      // Initialize the selected version ID with the default version ID
      if (!selectedVersionId.value) {
        selectedVersionId.value = id;
        // Clear selected version data so it falls back to finding the version
        selectedVersionData.value = null;
      }
    }

    // Get the currently selected version content (for processing status and video URLs)
    const selectedVersionContent = computed(() => {
      // If we have selected version data, use it
      if (selectedVersionData.value) {
        return selectedVersionData.value;
      }

      // If we have a selected version ID, try to find it in the internal content
      if (selectedVersionId.value) {
        const selectedVersion = internalContent.value.find(item => item.id === selectedVersionId.value);
        if (selectedVersion) {
          return selectedVersion;
        }
      }

      // Fallback to current image if no selected version found
      return currentImage.value;
    });

    // Enhanced document content that includes temporary URLs from processing status
    const currentDocumentContent = computed(() => {
      // Return the selected version content
      // DocumentPreview will handle processing status and temporary URLs internally
      return selectedVersionContent.value;
    });

    // Initialize processing status as a ref first
    const processingStatus = ref(null);
    const processingStatusLoading = ref(false);

    // Computed properties for video-specific URLs (now using selected version)
    const currentContentUrl = computed(() => {
      if (isCurrentContentVideo.value) {
        const content = selectedVersionContent.value;

        // Check processing status for videos
        if (processingStatus.value) {
          const { completedStates, video_hls_url } = processingStatus.value;

          // If process_hls is complete and we have video_hls_url, use it
          if (completedStates?.includes('process_hls') && video_hls_url) {
            return video_hls_url;
          }

          // If process_hls is complete but video_hls_url is null, use temp URL
          if (completedStates?.includes('process_hls') && !video_hls_url && content?.url) {
            return content.url; // Use temp URL until HLS is ready
          }
        }

        // Fallback to existing logic
        return content?.video_hls_url || content?.url;
      }
      // For images, use the regular url
      return selectedVersionContent.value?.url;
    });

    const videoPlayerOptions = computed(() => {
      if (!isCurrentContentVideo.value) return {};

      const content = selectedVersionContent.value;
      const aspectRatio = content?.aspect_ratio ? `${Math.round(content.aspect_ratio * 100)}:100` : '16:9';

      return {
        responsive: true,
        fluid: true,
        aspectRatio: aspectRatio
      };
    });

    // Create a ref for refetch interval that we can update later
    const processingRefetchInterval = ref(false);

    // Processing status query for the selected version (videos only)
    const {
      data: processingStatusData,
      isLoading: processingStatusLoadingQuery,
      refetch: refetchProcessingStatus
    } = useQuery({
      queryKey: ['contentProcessingStatus', selectedVersionId, currentContentType],
      queryFn: async () => {
        const contentId = selectedVersionId.value || currentImage.value?.id;
        const contentType = currentContentType.value;

        if (!contentId || contentType !== 'video') {
          return null;
        }

        try {
          const response = await ContentApi.getContentProcessingStatus({
            contentIds: [contentId],
            type: contentType
          });

          if (response.statusCode === 200 && response.data && response.data.length > 0) {
            return response.data[0]; // Return the first (and only) result
          }
          return null;
        } catch (error) {
          console.error('Failed to fetch processing status:', error);
          return null;
        }
      },
      enabled: computed(() => {
        const contentId = selectedVersionId.value || currentImage.value?.id;
        return !!contentId && currentContentType.value === 'video';
      }),
      refetchInterval: processingRefetchInterval,
      staleTime: 1000, // Consider data stale after 1 second
      cacheTime: 1000 * 30 // Keep in cache for 30 seconds
    });

    // Watch for changes in processing status data and update our ref
    watch(
      () => processingStatusData.value,
      newData => {
        processingStatus.value = newData;
      },
      { immediate: true }
    );

    // Watch for changes in processing status loading and update our ref
    watch(
      () => processingStatusLoadingQuery.value,
      newLoading => {
        processingStatusLoading.value = newLoading;
      },
      { immediate: true }
    );

    // Watch for changes in processing status and update refetch interval
    watch(
      () => processingStatus.value,
      (newStatus, oldStatus) => {
        // Only poll if HLS processing is not complete yet
        if (newStatus && !newStatus.completedStates?.includes('process_hls')) {
          processingRefetchInterval.value = 3000; // Poll every 3 seconds
        } else {
          processingRefetchInterval.value = false; // Stop polling

          // If processing just completed, invalidate content queries to refresh ImagesGrid
          if (newStatus && newStatus.completedStates?.includes('process_hls')) {
            const wasProcessing = oldStatus && !oldStatus.completedStates?.includes('process_hls');

            if (wasProcessing) {
              // Processing just completed - refresh the gallery
              queryClient.invalidateQueries({ queryKey: ['content'] });

              // Also emit an event to parent components
              emit('content-processing-completed', {
                contentId: selectedVersionId.value || currentImage.value?.id,
                contentType: currentContentType.value
              });
            }
          }
        }
      },
      { immediate: true }
    );

    // Check if video is currently processing (defined after processingStatus is set up)
    const isVideoProcessing = computed(() => {
      if (!isCurrentContentVideo.value || !processingStatus.value) return false;

      const { completedStates } = processingStatus.value;

      // Video is processing ONLY if process_hls is not in completed states
      // We don't care about overall status - only if HLS processing is done
      return !completedStates?.includes('process_hls');
    });

    // Document processing state - simplified since DocumentPreview handles the details
    const isDocumentProcessing = computed(() => {
      // DocumentPreview will handle the processing logic internally
      // This is just for template conditional rendering
      return false; // Let DocumentPreview determine processing state
    });

    const documentProcessingMessage = computed(() => {
      // DocumentPreview handles its own processing messages
      return 'Processing document...';
    });

    // Get processing message for display (defined after processingStatus is set up)
    const processingMessage = computed(() => {
      if (!isVideoProcessing.value || !processingStatus.value) return '';

      const { message, state } = processingStatus.value;

      if (state === 'process_hls') {
        return message || 'Processing video...';
      }

      return message || 'Preparing video...';
    });

    const queryClient = useQueryClient();

    // Store the default version ID received from VersionSelector
    const defaultVersionId = ref(null);

    // Store the original image ID for fallback
    const originalImageId = ref(null);

    // Find the original image ID from the current image's relations
    function findOriginalImageId() {
      if (!currentImage.value) return null;

      // If the current image is the original
      if (currentImage.value.is_original === true || currentImage.value.version_name === 'V0' || (!currentImage.value.origin_image_id && currentImage.value.stack_id)) {
        return currentImage.value.id;
      }

      // If the current image has an origin_image_id, that's the original
      if (currentImage.value.origin_image_id) {
        return currentImage.value.origin_image_id;
      }

      // Look for the original image in internalContent
      const stackRelatedImages = internalContent.value.filter(img => {
        // If current image has origin_image_id, find all images with same origin
        if (currentImage.value.origin_image_id) {
          return img.id === currentImage.value.origin_image_id || img.origin_image_id === currentImage.value.origin_image_id;
        }
        // If current image is the original, find all images pointing to it
        if (currentImage.value.stack_id) {
          return img.origin_image_id === currentImage.value.id;
        }
        return false;
      });

      // Find the original image in the related images
      const original = stackRelatedImages.find(img => img.is_original === true || img.version_name === 'V0' || (!img.origin_image_id && img.stack_id));

      return original ? original.id : null;
    }

    // Update originalImageId whenever currentImage changes
    watch(
      () => currentImage.value?.id,
      () => {
        originalImageId.value = findOriginalImageId();
      },
      { immediate: true }
    );

    // Use the selected version ID for comments
    const commentFetchId = computed(() => {
      // If we have a selected version ID, use it
      if (selectedVersionId.value) {
        return selectedVersionId.value;
      }

      // If no selected version ID but we have a default version ID from VersionSelector, use it
      if (defaultVersionId.value) {
        return defaultVersionId.value;
      }

      // Fallback to current image ID if no version IDs are available
      return currentImage.value?.id || null;
    });

    // ===== UNIFIED COMMENT SYSTEM =====
    // This system now supports both images and videos using the same API and UI
    // The content type is automatically detected and passed to the unified API functions

    // Query for fetching comments based on the selected version ID and content type
    const {
      data: imageComments, // This will hold the fetched comments array (renamed for backward compatibility)
      isLoading: commentsLoading,
      isError: commentsError
    } = useQuery({
      queryKey: ['galleryContentComments', commentFetchId, currentContentType],
      queryFn: async () => {
        const fetchId = commentFetchId.value;
        const contentType = currentContentType.value;

        if (!fetchId) {
          return []; // Return empty array if no ID to fetch with
        }

        try {
          // Use the unified comment API that supports both images and videos
          const comments = await ContentApi.getComments({
            id: fetchId,
            contentType: contentType
          });
          return comments || [];
        } catch (error) {
          console.error(`GalleryLightbox: Failed to fetch comments for ${contentType} ID ${fetchId}:`, error);
          throw error;
        }
      },
      enabled: computed(() => !!commentFetchId.value && !!currentContentType.value),
      placeholderData: ref([]),
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10
    });

    watch(
      () => [currentImage.value?.id, currentContentType.value],
      ([newId, newContentType], [oldId, oldContentType]) => {
        if (newId !== oldId || newContentType !== oldContentType) {
          // TanStack Query handles refetching automatically when a queryKey component changes and the query is enabled.
          // The query key includes both the ID and content type, so changes to either will trigger a refetch.
          // If newId is null/undefined, the query will become disabled.
        }
      }
    );

    // Watch for comments loading completion to set initial position at bottom
    watch(
      () => [imageComments.value, commentsLoading.value, activeTool.value],
      ([comments, loading, tool]) => {
        // Only set position when comments tool is active, comments are loaded, and we have comments
        if (tool === 'comments' && !loading && comments && comments.length > 0) {
          // Use nextTick to ensure DOM is fully rendered before setting position
          root.$nextTick(() => {
            if (commentsScrollArea.value) {
              try {
                const scrollTarget = commentsScrollArea.value.getScrollTarget();
                const maxScrollTop = scrollTarget.scrollHeight - scrollTarget.clientHeight;
                // Set initial position at bottom without any delay or animation
                scrollTarget.scrollTop = maxScrollTop;
              } catch (error) {
                // Fallback to the function with delay if direct setting fails
                scrollToLatestComment();
              }
            }
          });
        }
      },
      { flush: 'post' } // Execute after DOM updates
    );

    function updateContent(newContent) {
      internalContent.value = [...newContent];
    }

    // Helper function to update local modifications for the current image
    function updateCurrentImage(changes) {
      if (!currentImage.value || !currentImage.value.id) return;

      const imageId = currentImage.value.id;

      // Initialize modifications object for this image if needed
      if (!localImageModifications.value[imageId]) {
        localImageModifications.value[imageId] = {};
      }

      // Special handling for labels to ensure reactivity
      if (changes.labels) {
        // Force reactivity by creating a new object reference
        localImageModifications.value = {
          ...localImageModifications.value,
          [imageId]: {
            ...localImageModifications.value[imageId],
            labels: [...changes.labels] // Create new array reference
          }
        };

        // Also update the base image directly to ensure UI refresh
        if (internalContent.value[currentIndex.value]) {
          internalContent.value[currentIndex.value] = {
            ...internalContent.value[currentIndex.value],
            labels: [...changes.labels]
          };
        }
      } else {
        // Regular updates for other properties
        localImageModifications.value[imageId] = {
          ...localImageModifications.value[imageId],
          ...changes
        };
      }
    }

    // Method to update current image with safe deep copy
    function updateCurrentImageData(newImageData) {
      if (!currentImage.value || !currentImage.value.id) return;

      // Create deep copy of the new image data
      const safeData = JSON.parse(JSON.stringify(newImageData));

      // Update using local modifications
      updateCurrentImage(safeData);

      return currentImage.value;
    }

    // Tool toggle function
    function toggleTool(toolId) {
      // Simply set the active tool - no transitions needed
      activeTool.value = toolId;
    }

    // ===== UNIFIED LABELING SYSTEM =====
    // This system now supports both images and videos using the same API and UI
    // The content type is automatically detected and passed to the unified API functions

    async function handleAddLabel() {
      if (!newLabel.value.trim()) {
        labelError.value = 'Label cannot be empty';
        return;
      }

      // Store the new label text before clearing the input
      const newLabelText = newLabel.value.trim();

      // Get the current content type (image or video)
      const contentType = currentContentType.value;

      // Optimistically update UI immediately
      const currentLabels = currentImage.value.labels || [];
      // Make a copy of the array to ensure reactivity
      const optimisticLabels = [...currentLabels, newLabelText];
      updateCurrentImage({ labels: optimisticLabels });

      // Close the dialog and clear input right away for better UX
      showAddLabelDialog.value = false;
      newLabel.value = '';

      // Now do the actual API call in the background
      loadingLabel.value = true;
      labelError.value = '';
      try {
        const { ContentApi } = await import('@/api/modules/content');
        // Use the unified label API that supports both images and videos
        const updatedLabels = await ContentApi.addLabel({
          id: currentImage.value.id,
          label: newLabelText,
          contentType: contentType
        });

        // Update with server response
        updateCurrentImage({ labels: updatedLabels });
        const updatedImage = { ...currentImage.value, labels: updatedLabels };
        emit('image-updated', updatedImage);

        root.$amplitude.trackEvent('Tag Added (Lightbox)', {
          label: newLabelText
        });
      } catch (e) {
        // If it fails, revert to previous state
        updateCurrentImage({ labels: currentLabels });
        labelError.value = 'Failed to add label.';
        root.$q.notify({
          message: `Failed to add tag to ${contentType}`,
          color: 'negative',
          position: 'top',
          timeout: 2000
        });
      } finally {
        loadingLabel.value = false;
      }
    }

    // Direct removal of labels without confirmation
    async function removeLabel(label) {
      if (!label) return;

      // Get the current content type (image or video)
      const contentType = currentContentType.value;

      // Get current labels
      const currentLabels = currentImage.value.labels || [];

      // Optimistically update UI by filtering out the removed label
      const optimisticLabels = currentLabels.filter(l => l !== label);
      updateCurrentImage({ labels: optimisticLabels });

      // Now do the actual API call in the background
      try {
        const { ContentApi } = await import('@/api/modules/content');
        // Use the unified label API that supports both images and videos
        const updatedLabels = await ContentApi.removeLabel({
          id: currentImage.value.id,
          label,
          contentType: contentType
        });

        // Update with server response
        updateCurrentImage({ labels: updatedLabels });
        const updatedImage = { ...currentImage.value, labels: updatedLabels };
        emit('image-updated', updatedImage);
      } catch (e) {
        // If it fails, revert to previous state
        updateCurrentImage({ labels: currentLabels });
        root.$q.notify({
          message: `Failed to remove tag from ${contentType}`,
          color: 'negative',
          position: 'top',
          timeout: 2000
        });
      }
    }

    // Tag removal is now handled directly without confirmation dialog

    const handleKeyboardNavigation = e => {
      if (e.key === 'Escape') {
        emit('close');
      }
    };

    onMounted(() => {
      document.addEventListener('keydown', handleKeyboardNavigation);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('keydown', handleKeyboardNavigation);
    });

    async function getFileSizeInMB(url) {
      if (!url || url.startsWith('/placeholder.svg')) {
        imageSizeMB.value = 'N/A';
        return;
      }
      imageSizeMB.value = 'Calculating...';
      try {
        // Directly use GET request as HEAD request is causing CORS issues
        const response = await fetch(url);
        let fileSize;
        if (response.ok) {
          const blob = await response.blob();
          fileSize = blob.size;
        } else {
          imageSizeMB.value = 'Error';
          return;
        }

        if (fileSize) {
          const sizeInBytes = parseInt(fileSize, 10);
          if (sizeInBytes < 1024) {
            imageSizeMB.value = sizeInBytes + ' B';
          } else if (sizeInBytes < 1024 * 1024) {
            imageSizeMB.value = (sizeInBytes / 1024).toFixed(2) + ' KB';
          } else {
            imageSizeMB.value = (sizeInBytes / (1024 * 1024)).toFixed(2) + ' MB';
          }
        } else {
          imageSizeMB.value = 'Unknown';
        }
      } catch (error) {
        imageSizeMB.value = 'Error';
      }
    }

    // Helper functions for coordinated loading
    const preloadImage = async imageUrl => {
      if (!imageUrl) return Promise.resolve();

      return new Promise((resolve, reject) => {
        const img = new Image();
        const timeout = setTimeout(() => {
          reject(new Error('Image preload timeout'));
        }, 5000); // 5 second timeout

        img.onload = () => {
          clearTimeout(timeout);
          resolve();
        };

        img.onerror = () => {
          clearTimeout(timeout);
          // Don't reject on image error, just resolve to continue
          resolve();
        };

        img.src = imageUrl;
      });
    };

    // Coordinate loading states between components
    const coordinateLoadingStates = isLoading => {
      versionChangeInProgress.value = isLoading;
      if (isLoading) {
        imageLoaded.value = false;
        imageError.value = false;
      }
    };

    // Image handling methods
    const handleImageLoaded = () => {
      imageLoaded.value = true;
      imageError.value = false;
    };

    const handleImageError = () => {
      imageLoaded.value = false;
      imageError.value = true;
    };

    const retryLoadImage = () => {
      imageError.value = false;
      imageLoaded.value = false;

      // Instead of modifying the URL directly (which breaks S3 signatures),
      // let's force a reload by temporarily clearing and then resetting the image
      const originalUrl = currentImage.value.url;

      // Step 1: Temporarily clear the URL to force image unload
      updateCurrentImage({ url: '' });

      // Step 2: After a brief delay, restore the original URL to trigger reload
      setTimeout(() => {
        updateCurrentImage({ url: originalUrl });
      }, 50);
    };

    // Watch for currentIndex changes to reset image loading state
    watch(currentIndex, () => {
      if (currentImage.value && currentImage.value.url) {
        // Reset image loading state when user navigates to a different image
        imageLoaded.value = false;
        imageError.value = false;
      }
    });

    watchEffect(() => {
      if (currentImage.value && currentImage.value.url) {
        getFileSizeInMB(currentImage.value.url);
      }
    });

    // Extract file extension from filename
    function getFileExtension(filename) {
      if (!filename) return 'Unknown';
      const parts = filename.split('.');
      if (parts.length > 1) {
        const extension = parts[parts.length - 1].toUpperCase();
        return extension;
      }
      return 'Unknown';
    }

    // Format duration in seconds to MM:SS format
    function formatDuration(seconds) {
      if (!seconds) return '0:00';
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    // Comments logic - refactored to use TanStack Query's useMutation with unified API
    // Create a mutation for adding comments that works with both images and videos
    const addCommentMutation = useMutation({
      mutationFn: ({ id, content, contentType }) => ContentApi.addComment({ id, content, contentType }),

      // Optimistically update the UI
      onMutate: async ({ content, contentType }) => {
        // Use the updated query key that includes content type
        const queryKey = ['galleryContentComments', commentFetchId.value, contentType];

        // Cancel any outgoing refetches to avoid overwriting our optimistic update
        await queryClient.cancelQueries({ queryKey });

        // Snapshot the previous value
        const previousComments = queryClient.getQueryData(queryKey);

        // Create an optimistic comment with temporary ID and current timestamp
        const optimisticComment = {
          id: `temp-${Date.now()}`,
          content: content,
          user_name: root.$store.getters['auth/user']?.name || 'You',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        // Optimistically update the cache with our new comment
        queryClient.setQueryData(queryKey, old => {
          return old ? [optimisticComment, ...old] : [optimisticComment];
        });

        // Return context with the previous comments and query key
        return { previousComments, queryKey };
      },

      // If the mutation fails, roll back to the previous value
      onError: (err, variables, context) => {
        if (context?.previousComments && context?.queryKey) {
          queryClient.setQueryData(context.queryKey, context.previousComments);
        }

        // Show error notification
        root.$q.notify({
          message: 'Failed to add comment',
          color: 'negative',
          position: 'top',
          timeout: 2000
        });
      },

      // When the mutation succeeds, update with the actual server data
      onSuccess: (data, variables) => {
        // Invalidate the query to refetch with the actual server data
        const queryKey = ['galleryContentComments', commentFetchId.value, variables.contentType];
        queryClient.invalidateQueries({ queryKey });

        root.$amplitude.trackEvent('Comment Added (Lightbox)', {
          contentId: variables.id,
          comment: variables.content
        });
        // Scroll to show the latest comment
        scrollToLatestComment();
      },

      // Always run this code after the mutation (success or error)
      onSettled: () => {
        // Reset the comment input field
        commentInput.value = '';
      }
    });

    // Handler for adding a comment
    function handleAddComment() {
      if (!commentInput.value.trim()) return;

      // Determine which ID to use for the comment
      // Use the selected version ID if available, otherwise fall back to current image ID
      const commentTargetId = selectedVersionId.value || currentImage.value.id;

      // Get the current content type (image or video)
      const contentType = currentContentType.value;

      // Execute the mutation with the appropriate ID and content type
      addCommentMutation.mutate({
        id: commentTargetId,
        content: commentInput.value.trim(),
        contentType: contentType
      });
    }

    // Format comment date to a user-friendly format
    function formatCommentDate(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      // Show relative time for recent comments
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
      if (diffHours < 24) {
        // Format like "Today, 12:30 PM"
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = hours % 12 || 12;
        const formattedMinutes = minutes.toString().padStart(2, '0');
        return `Today, ${formattedHours}:${formattedMinutes} ${ampm}`;
      }
      if (diffDays < 2) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;

      // For older comments, show the date
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }

    // Check if a comment has been edited
    function isCommentEdited(comment) {
      return comment.updated_at && comment.created_at !== comment.updated_at;
    }

    // Function to scroll to the latest comment
    function scrollToLatestComment() {
      // Add a small delay to ensure DOM has updated
      setTimeout(() => {
        if (commentsScrollArea.value) {
          try {
            // Scroll to the bottom since newest comments are at the bottom
            const scrollTarget = commentsScrollArea.value.getScrollTarget();
            const maxScrollTop = scrollTarget.scrollHeight - scrollTarget.clientHeight;
            commentsScrollArea.value.setScrollPosition('vertical', maxScrollTop, 0); // No animation - immediate scroll
          } catch (error) {
            // Scroll area not ready yet
          }
        }
      }, 100);
    }

    // Format tag text to handle very long tags
    function formatTagText(tag) {
      if (!tag || typeof tag !== 'string') return tag;

      // Split into words and check word count
      const words = tag.trim().split(/\s+/);

      // If more than 20 words, truncate and add ellipsis
      if (words.length > 20) {
        return words.slice(0, 20).join(' ') + '...';
      }

      // If single word is extremely long (more than 50 characters), truncate
      if (words.length === 1 && tag.length > 50) {
        return tag.substring(0, 47) + '...';
      }

      // Return original tag if within reasonable limits
      return tag;
    }

    // Get CSS class for tag chip based on tag length
    function getTagChipClass(tag) {
      if (!tag || typeof tag !== 'string') return 'tag-chip';

      const baseClass = 'tag-chip';

      // For very long single words (more than 30 characters), use ellipsis approach
      // This handles URLs, very long compound words, etc.
      const words = tag.trim().split(/\s+/);
      if (words.length === 1 && tag.length > 30) {
        return `${baseClass} ellipsis`;
      }

      // For normal tags (including those with multiple words), use normal wrapping
      return baseClass;
    }

    // Add version control handlers
    const handleVersionCreated = ({ stackId }) => {
      // Update the current image with the stack ID
      if (currentImage.value) {
        updateCurrentImage({ stack_id: stackId });
      }

      // Notify the parent about the update
      emit('content-updated', { id: currentImage.value.id, changes: { stack_id: stackId } });
    };

    const handleVersionRestored = restoredVersionData => {
      // Early return if no current image
      if (!currentImage.value) return;

      // Check if this is a video content
      const isVideo = currentImage.value.type === 'video';

      // Capture the original ID for event emission
      const originalId = currentImage.value.id;

      // Base updates object - will hold all changes we need to make
      const updates = {};

      // Update name and metadata if available
      if (restoredVersionData.versionName) {
        updates.name = restoredVersionData.versionName;
      }

      // If we have complete version data, apply relevant fields
      if (restoredVersionData.versionData) {
        // Copy relevant metadata from restored version
        if (restoredVersionData.versionData.name) {
          updates.name = restoredVersionData.versionData.name;
        }

        if (restoredVersionData.versionData.filename) {
          updates.filename = restoredVersionData.versionData.filename;
        }

        // Update the selected version ID for comments
        if (restoredVersionData.versionData.id) {
          selectedVersionId.value = restoredVersionData.versionData.id;
        }

        // For video content - special handling to force refresh
        if (isVideo) {
          // For videos, just force a video player refresh by incrementing the key
          videoPlayerKey.value += 1;
        } else {
          // For images, update URL directly
          if (restoredVersionData.versionData.url) {
            // Reset image loading states
            imageLoaded.value = false;
            imageError.value = false;

            // Update the URL
            updates.url = restoredVersionData.versionData.url;
          }
        }
      } else if (isVideo) {
        // If we don't have full version data but need to refresh video
        const videoUrl = currentImage.value.video_hls_url || currentImage.value.url;
        const posterUrl = currentImage.value.preview_url;

        // Clear URLs temporarily
        updateCurrentImage({
          video_hls_url: '',
          url: '',
          preview_url: ''
        });

        // Restore with cache-busting parameters
        setTimeout(() => {
          const cacheBuster = `_t=${Date.now()}`;

          updateCurrentImage({
            video_hls_url: videoUrl + (videoUrl.includes('?') ? '&' : '?') + cacheBuster,
            url: !currentImage.value.video_hls_url ? videoUrl + (videoUrl.includes('?') ? '&' : '?') + cacheBuster : currentImage.value.url,
            preview_url: posterUrl + (posterUrl.includes('?') ? '&' : '?') + cacheBuster
          });
        }, 50);
      } else {
        // For images with no version data, just trigger a reload
        retryLoadImage();
      }

      // Apply non-video updates
      if (Object.keys(updates).length > 0) {
        updateCurrentImage(updates);
      }

      // Invalidate content queries to refresh ImagesGrid
      queryClient.invalidateQueries({ queryKey: ['content'] });

      // Notify parent component about the version change
      emit('version-changed', {
        originalId,
        versionId: restoredVersionData.versionData?.id || null,
        data: restoredVersionData
      });
    };

    // Handler for when user selects a different version from the VersionSelector
    const handleVersionChange = async newVersion => {
      if (!newVersion || !currentImage.value || versionChangeMutex.value) {
        return;
      }

      // Acquire mutex to prevent concurrent version changes
      versionChangeMutex.value = true;

      try {
        // Coordinate loading states
        coordinateLoadingStates(true);

        // Save the original image details
        const originalId = currentImage.value.id;
        const isVideo = currentImage.value.type === 'video';

        // Check if version was preloaded
        const isPreloaded = newVersion._loadingState?.isPreloaded;

        // If not preloaded, preload the image now
        if (!isPreloaded && !isVideo && newVersion.url) {
          try {
            await preloadImage(newVersion.url);
          } catch (error) {
            // Continue anyway
          }
        }

        // Check if this is a newly uploaded version (not in the original content array)
        const existsInContent = internalContent.value.some(item => item.id === newVersion.id);

        if (!existsInContent) {
          // This is a newly uploaded version - completely replace the current content item
          const currentIdx = currentIndex.value;
          if (currentIdx >= 0 && currentIdx < internalContent.value.length) {
            // Create a new content array with the new version replacing the current item
            const newContent = [...internalContent.value];
            const cleanVersion = { ...newVersion };
            delete cleanVersion._loadingState; // Remove loading state metadata
            newContent[currentIdx] = cleanVersion;
            internalContent.value = newContent;

            // Clear any local modifications for the old content
            if (localImageModifications.value[originalId]) {
              delete localImageModifications.value[originalId];
            }
          }
        } else {
          // This is an existing version - update properties as before
          // Create a copy of the new version properties, excluding the id and loading state
          const versionProperties = { ...newVersion };
          delete versionProperties.id;
          delete versionProperties._loadingState;

          // Apply all properties from the new version
          updateCurrentImage(versionProperties);
        }

        // Reset the image loading state for both cases
        isImageLoading.value = true;
        imageLoaded.value = false;

        if (isVideo) {
          // For videos, force a video player refresh by incrementing the key
          // This ensures a clean player instance for each version change
          videoPlayerKey.value += 1;

          // Also clear any processing status to force a fresh fetch
          processingStatus.value = null;
        }

        // Update the selected version ID and data for comments FIRST
        // This ensures comments are fetched for the selected version
        selectedVersionId.value = newVersion.id;
        const cleanVersionData = { ...newVersion };
        delete cleanVersionData._loadingState;
        selectedVersionData.value = cleanVersionData;

        // Coordinate loading states - mark as complete
        coordinateLoadingStates(false);

        // For videos, add a small delay to ensure proper cleanup before notifying parent
        if (isVideo) {
          setTimeout(() => {
            emit('version-changed', { originalId, versionId: newVersion.id });
            // Release mutex after video processing
            versionChangeMutex.value = false;
          }, 100);
        } else {
          // For images, notify immediately and release mutex
          emit('version-changed', { originalId, versionId: newVersion.id });
          versionChangeMutex.value = false;
        }
      } catch (error) {
        console.error('Error in version change:', error);
        // Reset states on error
        coordinateLoadingStates(false);
        versionChangeMutex.value = false;
      }
    };

    const handleVersionAdded = async ({ imageId, stackId }) => {
      if (!currentImage.value) return;

      // Update stack_id if needed
      if (!currentImage.value.stack_id && stackId) {
        updateCurrentImage({ stack_id: stackId });
      }

      // Invalidate content queries to refresh ImagesGrid immediately
      queryClient.invalidateQueries({ queryKey: ['content'] });

      // Trigger content refresh in parent
      // This will cause ImagesGrid to refresh data and update the lightbox
      emit('version-added', { imageId, stackId });

      // Force refresh by updating from props
      setTimeout(() => {
        const updatedImage = internalContent.value.find(img => img.id === imageId);
        if (updatedImage) {
          // Clear any existing modifications for this image
          if (currentImage.value.id && localImageModifications.value[currentImage.value.id]) {
            localImageModifications.value[currentImage.value.id] = {};
          }
        } else {
          // If image not found in props, add a refresh token to force reactivity
          updateCurrentImage({ __refreshToken: Date.now() });
        }
      }, 100);
    };

    const currentVersionName = computed(() => {
      // Default version name
      let versionName = 'V1';

      if (!currentImage.value) return versionName;

      // Get name directly from the current image
      if (currentImage.value.version_name && typeof currentImage.value.version_name === 'string' && !currentImage.value.version_name.includes('NaN')) {
        return currentImage.value.version_name;
      }

      // Fall back to a default name if we can't find a valid one
      return versionName;
    });

    // Computed property for original filename from the content array
    const originalFilename = computed(() => {
      // Fallback to current image filename or name
      if (currentImage.value) {
        return currentImage.value.filename || currentImage.value.name || 'Untitled';
      }

      return 'Untitled';
    });

    // Helper function to get content icon based on type
    const getContentIcon = contentType => {
      switch (contentType) {
        case 'video':
          return 'o_videocam';
        case 'document':
          return 'o_description';
        default:
          return 'o_image';
      }
    };

    const onPdfViewerLoaded = () => {
      // Do nothing
    };

    return {
      isOpen,
      currentIndex,
      currentImage,
      // Content type detection
      currentContentType,
      isCurrentContentVideo,
      // Video-specific properties
      currentContentUrl,
      videoPlayerOptions,
      // Comments related (from TanStack Query) - now unified for both images and videos
      imageComments, // Note: Despite the name, this now contains comments for both images and videos
      commentsLoading,
      commentsError,
      commentsScrollArea,
      scrollToLatestComment,
      selectedVersionId, // Expose the selected version ID
      selectedVersionData, // Expose the selected version data
      setDefaultVersionId, // Function to receive default version ID from VersionSelector
      handleKeyboardNavigation,
      // Labeling related (now unified for both images and videos)
      showAddLabelDialog,
      newLabel,
      loadingLabel,
      labelError,
      handleAddLabel, // Now supports both images and videos
      removeLabel, // Now supports both images and videos
      getFileExtension,
      formatDuration,
      imageSizeMB,
      activeTool,
      toggleTool,
      commentInput,
      addCommentMutation, // Expose the mutation for loading state in template
      handleAddComment,
      formatCommentDate,
      isCommentEdited,
      formatTagText,
      getTagChipClass,
      // formatDate is provided by the helpers mixin
      isDownloading,
      isImageLoading,
      imageLoaded,
      imageError,
      handleImageLoaded,
      handleImageError,
      retryLoadImage,
      versionChangeInProgress,
      coordinateLoadingStates,
      handleVersionCreated,
      handleVersionRestored,
      handleVersionChange,
      handleVersionAdded,
      updateCurrentImage,
      updateCurrentImageData,
      currentVersionName,
      // Expose the update content method
      updateContent,
      // Expose internal content for template use
      internalContent,
      videoPlayerKey,
      originalFilename,
      // Processing related
      processingStatus,
      processingStatusLoading,
      refetchProcessingStatus,
      isVideoProcessing,
      processingMessage,
      isDocumentProcessing,
      documentProcessingMessage,
      // Helper functions
      getContentIcon,
      onPdfViewerLoaded,
      currentDocumentContent
    };
  }
};
</script>

<style lang="scss" scoped>
@import './gallery/lightbox/gallery-lightbox.scss';
</style>

<style lang="scss">
/* Global styles for consistent gallery menu styling in lightbox */
/* Use the same gallery menu styling as ImagesGrid.vue for consistency */
.gallery-menu {
  max-width: 250px;
  max-height: none !important;
  height: auto !important;
  overflow-y: visible !important;

  .q-menu__content {
    max-height: none !important;
    height: auto !important;
    overflow-y: visible !important;
  }

  .q-virtual-scroll__content {
    max-height: none !important;
    height: auto !important;
    overflow-y: visible !important;
  }

  .q-item {
    padding: 8px 16px;
    min-height: 40px;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05) !important;
    }
  }

  .q-item-section + .q-item-section {
    margin-left: 8px;
  }

  .gallery-menu-label {
    white-space: nowrap;
  }
}

.custom-label {
  @include inter-font($size: 14px, $bolded: false, $color: var(--main-text-color));
  white-space: nowrap;
}
</style>
